import { useState, useEffect, useCallback } from "react";

// ── Types ────────────────────────────────────────────────────────────────────

interface UsageGroup {
  key: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  conversationCount: number;
  estimatedCostUsd: number;
}

interface UsageSummary {
  period: string;
  groupBy: string;
  groups: UsageGroup[];
  totals: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    conversationCount: number;
    estimatedCostUsd: number;
  };
}

type Period = "hour" | "day" | "week" | "month";
type GroupBy = "agent" | "provider" | "user";

// ── API helpers ──────────────────────────────────────────────────────────────

const BASE = "/api";

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("flax_token") ?? sessionStorage.getItem("flax_token");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function fetchUsage(period: Period, groupBy: GroupBy): Promise<UsageSummary> {
  const res = await fetch(`${BASE}/admin/usage?period=${period}&groupBy=${groupBy}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to fetch usage: ${res.status}`);
  return res.json();
}

// ── Formatting helpers ───────────────────────────────────────────────────────

function formatTokenCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toString();
}

function formatCost(usd: number): string {
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

function truncateKey(key: string, maxLen = 16): string {
  if (key.length <= maxLen) return key;
  return key.slice(0, maxLen - 3) + "...";
}

// ── Component ────────────────────────────────────────────────────────────────

export function UsageChart() {
  const [period, setPeriod] = useState<Period>("day");
  const [groupBy, setGroupBy] = useState<GroupBy>("agent");
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUsage = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const data = await fetchUsage(period, groupBy);
      setUsage(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load usage data");
    } finally {
      setLoading(false);
    }
  }, [period, groupBy]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6" data-testid="usage-chart">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Token Usage</h1>
          <p className="text-sm text-ink-500">Token consumption and cost analysis</p>
        </div>
        <button
          onClick={loadUsage}
          disabled={loading}
          className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-surface-2 disabled:opacity-50"
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4" data-testid="usage-controls">
        <div>
          <label className="block text-xs font-medium text-ink-500">Time Period</label>
          <div className="mt-1 flex rounded-lg border border-ink-200 bg-white">
            {(["hour", "day", "week", "month"] as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-sm capitalize ${
                  period === p
                    ? "bg-accent-600 text-white"
                    : "text-ink-600 hover:bg-surface-2"
                } first:rounded-l-lg last:rounded-r-lg`}
                data-testid={`period-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-500">Group By</label>
          <div className="mt-1 flex rounded-lg border border-ink-200 bg-white">
            {(["agent", "provider", "user"] as GroupBy[]).map((g) => (
              <button
                key={g}
                onClick={() => setGroupBy(g)}
                className={`px-3 py-1.5 text-sm capitalize ${
                  groupBy === g
                    ? "bg-accent-600 text-white"
                    : "text-ink-600 hover:bg-surface-2"
                } first:rounded-l-lg last:rounded-r-lg`}
                data-testid={`group-${g}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && !usage && (
        <div className="flex items-center justify-center py-12" data-testid="usage-loading">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-ink-300 border-t-accent-600" />
            <p className="mt-3 text-sm text-ink-500">Loading usage data...</p>
          </div>
        </div>
      )}

      {usage && (
        <>
          {/* Totals Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="usage-totals">
            <TotalCard
              title="Total Tokens"
              value={formatTokenCount(usage.totals.totalTokens)}
              subtitle={`${formatTokenCount(usage.totals.inputTokens)} in / ${formatTokenCount(usage.totals.outputTokens)} out`}
            />
            <TotalCard
              title="Conversations"
              value={usage.totals.conversationCount.toString()}
              subtitle={`In the last ${period}`}
            />
            <TotalCard
              title="Estimated Cost"
              value={formatCost(usage.totals.estimatedCostUsd)}
              subtitle="Based on Claude Sonnet pricing"
            />
            <TotalCard
              title="Avg Tokens/Conv"
              value={
                usage.totals.conversationCount > 0
                  ? formatTokenCount(
                      Math.round(usage.totals.totalTokens / usage.totals.conversationCount),
                    )
                  : "0"
              }
              subtitle="Average per conversation"
            />
          </div>

          {/* Bar Chart (CSS-based) */}
          <div className="rounded-lg border border-ink-100 bg-white p-4" data-testid="usage-bars">
            <h2 className="mb-4 text-lg font-semibold text-ink-800">
              Usage by {groupBy} ({period})
            </h2>

            {usage.groups.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-400">
                No usage data for the selected period
              </p>
            ) : (
              <div className="space-y-3">
                {usage.groups
                  .sort((a, b) => b.totalTokens - a.totalTokens)
                  .map((group) => {
                    const maxTokens = Math.max(
                      ...usage.groups.map((g) => g.totalTokens),
                      1,
                    );
                    const inputPercent =
                      (group.inputTokens / maxTokens) * 100;
                    const outputPercent =
                      (group.outputTokens / maxTokens) * 100;

                    return (
                      <div key={group.key} className="group" data-testid="usage-bar-row">
                        <div className="mb-1 flex items-center justify-between">
                          <span
                            className="text-sm font-medium text-ink-700"
                            title={group.key}
                          >
                            {truncateKey(group.key)}
                          </span>
                          <span className="text-xs text-ink-500">
                            {formatTokenCount(group.totalTokens)} tokens |{" "}
                            {formatCost(group.estimatedCostUsd)}
                          </span>
                        </div>
                        <div className="flex h-5 w-full overflow-hidden rounded bg-ink-50">
                          <div
                            className="bg-blue-400 transition-all duration-300"
                            style={{ width: `${inputPercent}%` }}
                            title={`Input: ${formatTokenCount(group.inputTokens)}`}
                          />
                          <div
                            className="bg-purple-400 transition-all duration-300"
                            style={{ width: `${outputPercent}%` }}
                            title={`Output: ${formatTokenCount(group.outputTokens)}`}
                          />
                        </div>
                      </div>
                    );
                  })}

                {/* Legend */}
                <div className="mt-3 flex items-center gap-4 border-t border-ink-50 pt-3">
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded bg-blue-400" />
                    <span className="text-xs text-ink-500">Input tokens</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded bg-purple-400" />
                    <span className="text-xs text-ink-500">Output tokens</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Detailed Table */}
          <div className="overflow-hidden rounded-lg border border-ink-100 bg-white" data-testid="usage-table">
            <table className="w-full text-sm">
              <thead className="border-b border-ink-100 bg-surface-2">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                    {groupBy}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-500">
                    Input Tokens
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-500">
                    Output Tokens
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-500">
                    Total
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-500">
                    Conversations
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-500">
                    Est. Cost
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {usage.groups
                  .sort((a, b) => b.totalTokens - a.totalTokens)
                  .map((group) => (
                    <tr key={group.key} className="hover:bg-surface-2">
                      <td className="px-4 py-3 font-medium text-ink-800" title={group.key}>
                        {truncateKey(group.key, 24)}
                      </td>
                      <td className="px-4 py-3 text-right text-ink-600">
                        {formatTokenCount(group.inputTokens)}
                      </td>
                      <td className="px-4 py-3 text-right text-ink-600">
                        {formatTokenCount(group.outputTokens)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-ink-800">
                        {formatTokenCount(group.totalTokens)}
                      </td>
                      <td className="px-4 py-3 text-right text-ink-600">
                        {group.conversationCount}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-ink-800">
                        {formatCost(group.estimatedCostUsd)}
                      </td>
                    </tr>
                  ))}
                {usage.groups.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-ink-400">
                      No usage data
                    </td>
                  </tr>
                )}
              </tbody>
              {usage.groups.length > 0 && (
                <tfoot className="border-t-2 border-ink-200 bg-surface-2">
                  <tr className="font-semibold">
                    <td className="px-4 py-3 text-ink-800">Total</td>
                    <td className="px-4 py-3 text-right text-ink-800">
                      {formatTokenCount(usage.totals.inputTokens)}
                    </td>
                    <td className="px-4 py-3 text-right text-ink-800">
                      {formatTokenCount(usage.totals.outputTokens)}
                    </td>
                    <td className="px-4 py-3 text-right text-ink-800">
                      {formatTokenCount(usage.totals.totalTokens)}
                    </td>
                    <td className="px-4 py-3 text-right text-ink-800">
                      {usage.totals.conversationCount}
                    </td>
                    <td className="px-4 py-3 text-right text-ink-800">
                      {formatCost(usage.totals.estimatedCostUsd)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Cost Estimation Note */}
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700">
            Cost estimates are based on approximate Claude Sonnet pricing ($3/M input, $15/M output).
            Actual costs depend on your provider and plan. Prices shown are for reference only.
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function TotalCard({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string;
  subtitle: string;
}) {
  return (
    <div className="rounded-lg border border-ink-100 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-500">{title}</p>
      <p className="mt-1 text-2xl font-bold text-ink-900">{value}</p>
      <p className="mt-1 text-xs text-ink-400">{subtitle}</p>
    </div>
  );
}
